const ServiceHours = {
  elements: [],
  endpoint: null,
  refreshTimer: null,

  init() {
    this.elements = [...document.querySelectorAll('[data-service-hours]')]

    if (this.elements.length === 0) {
      return
    }

    this.endpoint = this.elements[0].dataset.serviceHoursEndpoint

    if (!this.endpoint) {
      return
    }

    this.load()
  },

  getDateParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date)

    return Object.fromEntries(parts.map(({ type, value }) => [type, value]))
  },

  getDayDifference(from, to, timeZone) {
    const fromParts = this.getDateParts(from, timeZone)
    const toParts = this.getDateParts(to, timeZone)

    const fromDay = Date.UTC(Number(fromParts.year), Number(fromParts.month) - 1, Number(fromParts.day))
    const toDay = Date.UTC(Number(toParts.year), Number(toParts.month) - 1, Number(toParts.day))

    return Math.round((toDay - fromDay) / 86400000)
  },

  formatTime(date, locale, timeZone) {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date)
  },

  formatRelativeTime(date, locale) {
    const difference = date.getTime() - Date.now()
    const minutes = Math.max(1, Math.round(difference / 60000))
    const formatter = new Intl.RelativeTimeFormat(locale, {
      numeric: 'always',
    })

    if (minutes < 60) {
      return formatter.format(minutes, 'minute')
    }

    return formatter.format(Math.max(1, Math.round(minutes / 60)), 'hour')
  },

  getStatusText(availability, service, labels) {
    const locale = document.documentElement.lang || 'nl'
    const timeZone = availability.timezone || 'Europe/Amsterdam'
    const now = new Date()

    if (service.is_open) {
      const closesAt = service.current_period?.closes_at || service.next_change_at

      if (!closesAt) {
        return labels.open
      }

      const date = new Date(closesAt)
      const difference = date.getTime() - now.getTime()

      if (difference <= 2 * 60 * 60 * 1000) {
        return `${labels.open} · ${labels.closes} ${this.formatRelativeTime(date, locale)}`
      }

      return `${labels.open} · ${labels.closes} ${labels.at} ${this.formatTime(date, locale, timeZone)}`
    }

    const opensAt = service.next_open_at || service.next_change_at

    if (!opensAt) {
      return labels.closed
    }

    const date = new Date(opensAt)
    const difference = date.getTime() - now.getTime()

    if (difference <= 2 * 60 * 60 * 1000) {
      return `${labels.closed} · ${labels.opens} ${this.formatRelativeTime(date, locale)}`
    }

    const dayDifference = this.getDayDifference(now, date, timeZone)
    const time = this.formatTime(date, locale, timeZone)

    if (dayDifference === 0) {
      return `${labels.closed} · ${labels.opens} ${labels.at} ${time}`
    }

    if (dayDifference === 1) {
      return `${labels.closed} · ${labels.opens} ${labels.tomorrow} ${labels.at} ${time}`
    }

    const weekday = new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'long',
    }).format(date)

    return `${labels.closed} · ${labels.opens} ${weekday} ${labels.at} ${time}`
  },

  render(element, availability) {
    const service = availability[element.dataset.serviceHoursService]
    const indicator = element.querySelector('[data-service-hours-indicator]')
    const label = element.querySelector('[data-service-hours-label]')
    const labels = JSON.parse(element.dataset.serviceHoursLabels)

    if (!service || !indicator || !label) {
      return
    }

    indicator.classList.toggle('bg-success', service.is_open)
    indicator.classList.toggle('bg-secondary', !service.is_open)

    if (element.dataset.serviceHoursExpanded === 'true') {
      label.textContent = this.getStatusText(availability, service, labels)
    } else {
      label.textContent = service.is_open ? labels.open : labels.closed
    }
  },

  scheduleRefresh(availability) {
    if (this.refreshTimer) {
      window.clearTimeout(this.refreshTimer)
    }

    const changes = this.elements
      .map((element) => availability[element.dataset.serviceHoursService]?.next_change_at)
      .filter(Boolean)
      .map((date) => new Date(date).getTime())
      .filter((timestamp) => timestamp > Date.now())

    if (changes.length === 0) {
      return
    }

    const nextChange = Math.min(...changes)

    // The endpoint is cached briefly, so wait until the cached response has expired.
    const delay = nextChange - Date.now() + 35000

    this.refreshTimer = window.setTimeout(() => this.load(), delay)
  },

  renderUnavailable() {
    this.elements.forEach((element) => {
      const label = element.querySelector('[data-service-hours-label]')

      if (!label) {
        return
      }

      const labels = JSON.parse(element.dataset.serviceHoursLabels)

      label.textContent = labels.unknown
    })
  },

  async load() {
    try {
      const response = await fetch(this.endpoint, {
        headers: {
          Accept: 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Availability request failed with status ${response.status}`)
      }

      const availability = await response.json()

      this.elements.forEach((element) => this.render(element, availability))
      this.scheduleRefresh(availability)
    } catch {
      this.renderUnavailable()
    }
  },
}

export default ServiceHours
